import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  ButtonBase,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import SensorsIcon from '@mui/icons-material/Sensors';
import StorageIcon from '@mui/icons-material/Storage';
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined';
import { PRODUCT_STATUS_CONFIG, PRODUCT_STATUS_ORDER } from './constants.js';
import { money } from './drawerFormat.js';
import {
  formatEvidenceDate,
  productCardDate,
  sortEvidenceProducts,
} from './DrawerEvidenceHelpers.jsx';

const evidenceProductId = (option = {}) => {
  if (option.source === 'dpe') return option.referenceId || option.orderItemId || '';
  return option.orderItemId || option.referenceId || '';
};

const cardMeta = (option = {}) => [
  option.productCode,
  formatEvidenceDate(productCardDate(option.row || option, option.status)),
  evidenceProductId(option),
].filter(Boolean).join(' - ');

const numericPrice = (value) => {
  if (value === null || value === undefined || value === '') return 0;
  const parsed = Number(String(value).replace(/[$,]/g, ''));
  return Number.isFinite(parsed) ? parsed : 0;
};

const evidenceProductName = (item = {}) => (
  item.productName
  || item.serviceItem
  || item.serviceItemName
  || item.service_item
  || item.productCode
);

const sameId = (left, right) => {
  const a = String(left || '').trim();
  const b = String(right || '').trim();
  return Boolean(a && b && a === b);
};

const sameProductCode = (left, right) => {
  const leftCode = String(left || '').trim().toUpperCase();
  const rightCode = String(right || '').trim().toUpperCase();
  return Boolean(leftCode && rightCode && leftCode === rightCode);
};

const matchesCurrentEvidence = (option, currentEvidence) => {
  const productMatches = sameProductCode(option.productCode, currentEvidence.productCode);
  if (option.source === 'appsheet') return productMatches && sameId(option.orderItemId, currentEvidence.orderItemId);
  if (option.source === 'dpe') {
    return productMatches && (
      sameId(option.referenceId, currentEvidence.referenceId)
      || (!currentEvidence.referenceId && sameId(option.orderItemId, currentEvidence.orderItemId))
    );
  }
  return productMatches && (
    sameId(option.orderItemId, currentEvidence.orderItemId)
    || sameId(option.referenceId, currentEvidence.referenceId)
  );
};

const isSelectedEvidence = ({ option, selectedKey, currentEvidence }) => (
  selectedKey ? selectedKey === option.key : matchesCurrentEvidence(option, currentEvidence)
);

export const dpeEvidenceOptions = (items = []) => sortEvidenceProducts(items).map((product, index) => ({
  key: `dpe-${product.referenceId || product.orderItemId || product.productCode || 'row'}-${index}`,
  source: 'dpe',
  sourceLabel: 'DPE API',
  productName: evidenceProductName(product),
  productCode: product.productCode,
  price: numericPrice(product.dealerPrice),
  status: product.productStatus,
  orderItemId: product.orderItemId || '',
  referenceId: product.referenceId || '',
  row: product,
}));

export const appsheetEvidenceOptions = (data = {}) => {
  const rows = Array.isArray(data?.orderItems) ? data.orderItems : [];
  return PRODUCT_STATUS_ORDER.flatMap((status) => (
    sortEvidenceProducts(rows.filter((item) => (item.status || 'CANCELLED') === status))
  )).map((item, index) => ({
    key: `appsheet-${item.id || item.referenceId || item.productCode || 'row'}-${index}`,
    source: 'appsheet',
    sourceLabel: 'AppSheet',
    productName: evidenceProductName(item),
    productCode: item.productCode,
    price: numericPrice(item.mrr),
    status: item.status,
    orderItemId: item.id || '',
    referenceId: item.referenceId || '',
    salesforceAccountId: item.salesforceAccountId || item.accountId || '',
    salesforceAccountName: item.salesforceAccountName || item.accountName || item.dealerName || '',
    row: item,
  }));
};

const rowPrice = (row = {}, fields = []) => {
  for (const field of fields) {
    if (row[field] !== undefined && row[field] !== null && row[field] !== '') return numericPrice(row[field]);
  }
  return 0;
};

export const gmEvidenceOptions = (rows = []) => sortEvidenceProducts(rows).map((row, index) => ({
  key: `gm-${row.sourceRowKey || row.rowKey || row.id || row.rowNumber || row.productCode || 'row'}-${index}`,
  source: 'gm',
  sourceLabel: 'GM file',
  productName: row.productSelection || row.productName || row.productCode,
  productCode: row.productCode,
  price: rowPrice(row, ['dealerCost', 'price']),
  status: row.productStatus,
  orderItemId: row.accountMatch?.orderItemId || row.orderItemId || row.__orderItemId || '',
  referenceId: row.accountMatch?.referenceId || row.referenceId || '',
  salesforceAccountId: row.accountMatch?.salesforceAccountId || row.salesforceAccountId || '',
  salesforceAccountName: row.accountMatch?.salesforceAccountName || row.salesforceAccountName || row.dealerName || '',
  row,
}));

export const cpqEvidenceOptions = (rows = []) => sortEvidenceProducts(rows).map((row, index) => ({
  key: `cpq-${row.sourceRowKey || row.rowKey || row.id || row.rowNumber || row.oemProductCode || row.productCode || 'row'}-${index}`,
  source: 'cpq',
  sourceLabel: 'CPQ file',
  productName: row.productName || row.oemProductCode || row.productCode,
  productCode: row.oemProductCode || row.productCode || row.normalizedProductCode,
  price: rowPrice(row, ['budget', 'price']),
  status: row.productStatus,
  orderItemId: row.matchedAppSheetOrderItemId || row.accountMatch?.orderItemId || '',
  sourceOrderItemId: row.orderItemId || row.__orderItemId || '',
  matchedAppSheetOrderItemId: row.matchedAppSheetOrderItemId || row.accountMatch?.orderItemId || '',
  referenceId: row.referenceId || row.accountMatch?.referenceId || '',
  salesforceAccountId: row.salesforceAccountId || row.accountMatch?.salesforceAccountId || '',
  salesforceAccountName: row.salesforceAccountName || row.accountMatch?.salesforceAccountName || row.dealerName || '',
  row,
}));

const EvidenceCard = ({ option, selected, onSelect }) => {
  const statusCfg = PRODUCT_STATUS_CONFIG[option.status] || {};
  return (
    <ButtonBase
      onClick={() => onSelect(option)}
      aria-pressed={selected}
      sx={{
        display: 'block',
        width: '100%',
        textAlign: 'left',
        borderRadius: 1,
        p: 1,
        border: '1px solid',
        borderColor: selected ? 'primary.main' : 'divider',
        borderLeft: '4px solid',
        borderLeftColor: selected ? 'primary.main' : statusCfg.border || 'divider',
        bgcolor: selected ? 'primary.lighter' : 'action.hover',
        '&:hover': {
          bgcolor: selected ? 'primary.lighter' : 'action.selected',
          borderColor: selected ? 'primary.main' : 'primary.light',
        },
      }}
    >
      <Stack direction="row" spacing={1} alignItems="flex-start">
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 800, lineHeight: 1.25 }} noWrap>
            {option.productName || option.productCode || 'Product'}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }} noWrap>
            {cardMeta(option)}
          </Typography>
        </Box>
        <Typography variant="body2" sx={{ fontWeight: 900, fontVariantNumeric: 'tabular-nums' }}>
          {money(option.price)}
        </Typography>
      </Stack>
    </ButtonBase>
  );
};

const VarianceEvidencePicker = ({
  gmItems = [],
  cpqItems = [],
  dpeItems = [],
  appsheetData = null,
  selectedEvidence = null,
  currentOrderItemId = '',
  currentReferenceId = '',
  currentProductCode = '',
  onSelectEvidence,
  maxListHeight = 260,
  pageSize = 80,
}) => {
  const [tab, setTab] = useState('appsheet');
  const [visibleCount, setVisibleCount] = useState(pageSize);
  const gmOptions = useMemo(() => gmEvidenceOptions(gmItems), [gmItems]);
  const cpqOptions = useMemo(() => cpqEvidenceOptions(cpqItems), [cpqItems]);
  const dpeOptions = useMemo(() => dpeEvidenceOptions(dpeItems), [dpeItems]);
  const appsheetOptions = useMemo(() => appsheetEvidenceOptions(appsheetData), [appsheetData]);
  const currentEvidence = useMemo(() => ({
    orderItemId: currentOrderItemId,
    referenceId: currentReferenceId,
    productCode: currentProductCode,
  }), [currentOrderItemId, currentProductCode, currentReferenceId]);
  const preferredTab = selectedEvidence?.source || (currentOrderItemId ? 'appsheet' : currentReferenceId ? 'dpe' : 'appsheet');
  const activeOptions = useMemo(() => {
    if (tab === 'dpe') return dpeOptions;
    if (tab === 'gm') return gmOptions;
    if (tab === 'cpq') return cpqOptions;
    return appsheetOptions;
  }, [appsheetOptions, cpqOptions, dpeOptions, gmOptions, tab]);

  useEffect(() => {
    setTab(preferredTab);
  }, [preferredTab]);

  useEffect(() => {
    setVisibleCount(pageSize);
  }, [activeOptions, pageSize]);

  const selectedKey = selectedEvidence?.key || '';
  const orderedOptions = useMemo(() => {
    return [...activeOptions].sort((left, right) => {
      const leftSelected = isSelectedEvidence({ option: left, selectedKey, currentEvidence });
      const rightSelected = isSelectedEvidence({ option: right, selectedKey, currentEvidence });
      if (leftSelected === rightSelected) return 0;
      return leftSelected ? -1 : 1;
    });
  }, [activeOptions, currentEvidence, selectedKey]);
  const visibleOptions = orderedOptions.slice(0, visibleCount);
  const hiddenCount = Math.max(0, orderedOptions.length - visibleOptions.length);

  return (
    <Box>
      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 800 }}>
        Evidence inheritance
      </Typography>
      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1 }}>
        Pick a product card to stamp account, order item, reference, product code, and price evidence into this variance action.
      </Typography>
      <Tabs value={tab} onChange={(_event, value) => setTab(value)} variant="fullWidth" sx={{ minHeight: 34, mb: 1, '& .MuiTab-root': { minHeight: 34, fontSize: 12, textTransform: 'none' } }}>
        <Tab value="appsheet" label={`AppSheet (${appsheetOptions.length})`} icon={<StorageIcon fontSize="small" />} iconPosition="start" />
        <Tab value="dpe" label={`DPE (${dpeOptions.length})`} icon={<SensorsIcon fontSize="small" />} iconPosition="start" />
        <Tab value="gm" label={`GM (${gmOptions.length})`} icon={<TableChartOutlinedIcon fontSize="small" />} iconPosition="start" />
        <Tab value="cpq" label={`CPQ (${cpqOptions.length})`} icon={<TableChartOutlinedIcon fontSize="small" />} iconPosition="start" />
      </Tabs>
      <Stack spacing={0.75} sx={{ maxHeight: maxListHeight, overflowY: 'auto', pr: 0.5 }}>
        {orderedOptions.length ? visibleOptions.map((option) => (
          <EvidenceCard
            key={option.key}
            option={option}
            selected={isSelectedEvidence({ option, selectedKey, currentEvidence })}
            onSelect={onSelectEvidence}
          />
        )) : (
          <Alert severity="info">No {tab} products available for this BAC.</Alert>
        )}
        {hiddenCount > 0 && (
          <ButtonBase
            onClick={() => setVisibleCount((count) => count + pageSize)}
            sx={{
              width: '100%',
              border: '1px dashed',
              borderColor: 'divider',
              borderRadius: 1,
              p: 1,
              color: 'primary.main',
              fontWeight: 900,
            }}
          >
            Show {Math.min(pageSize, hiddenCount)} more products
          </ButtonBase>
        )}
      </Stack>
    </Box>
  );
};

export default VarianceEvidencePicker;
